(function () {
  const layouts = [
    { selector: 'product-card', target: '.product_card_wrapper' },
    { selector: '.custom-product-card' },
    { selector: '.spotlight-custom-card' },
    { selector: '.promo-card-inner' },
    { selector: '.product_tab__cards_wrpr' },
    { selector: '.pdp_cards_content' },
    { selector: '.premium-section .product-card' },
    { selector: '.ftd_pdp_info', panel: true }
  ];
  const railSelector = '.swiper-wrapper, .th-products-track, .popular_products_inner, .product-card-grid-mobile, .scroll-track, .cs-products, .spotlight-column, .promo-hub-grid, .cart_recommendations_rail, .product-display';
  const contentSelector = '.product_card_info, .custom-product-content, .card-product-tags, .product-card-title, .custom-product-title, .pdp_title';
  const gallerySelector = '.product-card-swiper, .product-card-media-dot, .product-thumb';
  const galleryStateClasses = ['is-active', 'swiper-slide-active', 'swiper-slide-next', 'swiper-slide-prev', 'swiper-slide-visible', 'swiper-slide-fully-visible'];

  function maximumCardHeights(cards) {
    const maxima = new Map();
    for (const card of cards) maxima.set(card.group || card.rail, Math.max(maxima.get(card.group || card.rail) || 0, card.height));
    return cards.map(card => Math.ceil(maxima.get(card.group || card.rail)));
  }

  class ProductCardHeightLayout {
    constructor(options = {}) {
      this.layouts = options.layouts || layouts;
      this.railSelector = options.railSelector || railSelector;
      this.contentSelector = options.contentSelector || contentSelector;
      this.gallerySelector = options.gallerySelector || gallerySelector;
      this.galleryStateClasses = new Set(options.galleryStateClasses || galleryStateClasses);
      this.cardSelector = this.layouts.map(layout => layout.selector).join(', ');
    }

    collect(root) {
      const cards = [];
      for (const layout of this.layouts) {
        for (const card of root.querySelectorAll(layout.selector)) {
          const rail = card.parentElement?.closest(this.railSelector);
          if (layout.panel && !rail) continue;
          if (rail?.matches('.swiper-wrapper') &&
            !rail.parentElement?.matches('.swiper-initialized, .featured-collection-swiper')) continue;
          const target = layout.target ? card.querySelector(layout.target) : card;
          if (target) cards.push({ target, group: rail || card.parentElement });
        }
      }
      return cards;
    }

    affectsLayout(record) {
      const element = record.target.nodeType === 1 ? record.target : record.target.parentElement;
      if (!element) return false;
      if (record.type === 'attributes' && record.attributeName === 'class' &&
        element.closest(this.gallerySelector)) {
        const previous = new Set((record.oldValue || '').split(/\s+/).filter(Boolean));
        const current = new Set((element.getAttribute('class') || '').split(/\s+/).filter(Boolean));
        const changed = [...previous, ...current].filter(name => previous.has(name) !== current.has(name));
        if (changed.every(name => this.galleryStateClasses.has(name))) return false;
      }
      if (element.closest(this.cardSelector) || element.querySelector(this.cardSelector)) return true;
      return Array.from(record.removedNodes || []).some(node => node.nodeType === 1 &&
        (node.matches(this.cardSelector) || node.querySelector(this.cardSelector)));
    }

    measure(cards) {
      for (const { target } of cards) {
        target.setAttribute('data-card-height-managed', '');
        target.setAttribute('data-card-height-measuring', '');
      }
      try {
        return cards.map(entry => ({ ...entry, width: entry.target.offsetWidth, height: entry.target.offsetHeight }))
          .filter(entry => entry.width > 0 && entry.height > 0);
      } finally {
        for (const { target } of cards) target.removeAttribute('data-card-height-measuring');
      }
    }

    apply(cards, heights) {
      cards.forEach((entry, index) => {
        const height = `${heights[index]}px`;
        if (entry.target.style.getPropertyValue('--product-card-max-height') !== height) {
          entry.target.style.setProperty('--product-card-max-height', height);
        }
      });
    }

    clear(target) {
      target.removeAttribute('data-card-height-managed');
      target.removeAttribute('data-card-height-measuring');
      target.style.removeProperty('--product-card-max-height');
    }

    observedElements(targets) {
      const elements = new Set(targets);
      for (const target of targets) {
        for (const content of target.querySelectorAll(this.contentSelector)) {
          elements.add(content);
        }
      }
      return elements;
    }
  }

  class ProductCardHeightController {
    constructor(root = document, options = {}) {
      this.root = root;
      this.layout = options.layout || new ProductCardHeightLayout();
      this.calculateHeights = options.calculateHeights || maximumCardHeights;
      this.runtime = options.runtime || root.defaultView || root.ownerDocument?.defaultView || window;
      this.fonts = (root.ownerDocument || root).fonts;
      this.targets = new Set();
      this.observed = new Set();
      this.connected = false;
      this.frame = null;
      this.schedule = () => {
        if (!this.connected || this.frame !== null) return;
        this.frame = this.runtime.requestAnimationFrame(() => {
          this.frame = null;
          if (this.connected) this.update();
        });
      };
      this.resizeObserver = new this.runtime.ResizeObserver(this.schedule);
      this.mutationObserver = new this.runtime.MutationObserver(records => {
        if (records.some(record => this.layout.affectsLayout(record))) this.schedule();
      });
    }

    connect() {
      if (this.connected) return;
      this.connected = true;
      this.mutationObserver.observe(this.root, {
        subtree: true, childList: true, characterData: true,
        attributes: true, attributeOldValue: true, attributeFilter: ['class', 'hidden', 'open']
      });
      this.runtime.addEventListener('resize', this.schedule);
      this.root.addEventListener('load', this.schedule, true);
      this.fonts?.addEventListener('loadingdone', this.schedule);
      this.fonts?.ready.then(this.schedule);
      this.schedule();
    }

    update() {
      const cards = this.layout.collect(this.root);
      const nextTargets = new Set(cards.map(entry => entry.target));
      for (const target of this.targets) {
        if (!nextTargets.has(target)) this.layout.clear(target);
      }
      const measured = this.layout.measure(cards);
      this.layout.apply(measured, this.calculateHeights(measured));
      this.observe(this.layout.observedElements(nextTargets));
      this.targets = nextTargets;
    }

    observe(nextObserved) {
      for (const element of this.observed) {
        if (!nextObserved.has(element)) this.resizeObserver.unobserve(element);
      }
      for (const element of nextObserved) {
        if (!this.observed.has(element)) this.resizeObserver.observe(element);
      }
      this.observed = nextObserved;
    }

    disconnect() {
      this.connected = false;
      this.mutationObserver.disconnect();
      this.resizeObserver.disconnect();
      this.runtime.removeEventListener('resize', this.schedule);
      this.root.removeEventListener('load', this.schedule, true);
      this.fonts?.removeEventListener('loadingdone', this.schedule);
      if (this.frame !== null) this.runtime.cancelAnimationFrame(this.frame);
      this.frame = null;
      for (const target of this.targets) {
        this.layout.clear(target);
      }
      this.targets.clear();
      this.observed.clear();
    }
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { maximumCardHeights, ProductCardHeightLayout, ProductCardHeightController };
  } else {
    const controller = new ProductCardHeightController();
    controller.connect();
  }
})();
